import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  isEmptyArray,
  isEmptyString,
  getUniqArray,
  isEmptyObject,
} from '@common/utils/index';

// 전체페이지 수 (1.1,1.2,2.1,3.1)
const TOTAL_PAGES_NUMBER = 4;

/**
 * 적합성 테스트 폼 Lnb
 */
const ForeignCurrencySuitabilityTestLnb = ({
  customerInfo,
  currentStep,
  setCurrentStep,
  completedStepList,
  setCompletedStepList,
  showModal,
  handleCloseModalButtonClick,
  isShowLnb,
}) => {
  const { isPortrait } = $hooks.useDevice();
  const [stepList] = useState([
    {
      main: '1. 본인 인증 및 서명 동의',
      subList: ['주요 내용 확인 및 본인인증', '전자문서 서명 동의'],
    },
    { main: '2. 외화보험 적합성 진단', subList: ['외화보험 적합성 진단'] },
    { main: '3. 외화보험 적합성 진단 결과 확인', subList: [] },
  ]);

  /**
   * main menu 완료 여부 (하위 메뉴가 모드 완료 시 완료)
   */
  const isMainCompleted = useCallback(
    (completedStepList, stepList, mainIdx) => {
      const menuLen =
        stepList.find((v, i) => i === mainIdx).subList.length || 1;
      const completedLen = getUniqArray(completedStepList).filter(
        l => l.main === mainIdx + 1,
      ).length;
      const result = completedLen === menuLen;

      return result;
    },
    [],
  );

  const handleMainClick = useCallback(
    (cusomerInfo, completedStepList, currentStep, targetStep) => {
      const validateStep = (cusomerInfo, completedStepList, targetStep) => {
        // 이전 단계로 이동인지 여부
        const isMoveBack = !isEmptyObject(
          completedStepList.find(
            l => targetStep.main === l.main && targetStep.sub === l.sub,
          ),
        );
        // 2. 외화보험 적합성 진단
        if (2 === targetStep.main && 1 === targetStep.sub) {
          // 본인인증 및 서명동의 여부 확인 (완료 목록에 1단계가 2개면 본인인증 된건로 처리)
          const isValid =
            getUniqArray(completedStepList).filter(l => l.main === 1).length ===
              2 ?? false;

          return {
            isValid,
            msg: isValid
              ? ''
              : '본인 인증 및 서명동의를 수행하지 않아 외화보험 적합성 진단이 불가합니다.',
            isMoveBack,
          };
        }
        // 3. 외화보험 적합성 진단 결과 확인
        else if (3 === targetStep.main && 1 === targetStep.sub) {
          const isValid =
            TOTAL_PAGES_NUMBER - 1 === (completedStepList?.length ?? 0) && // 완료목록이 3개 인지 확인 (1.1,1.2,2.1)
            !isEmptyString(customerInfo.suitability?.suitabilityStatus); // 진단 결과 존재 여부 확인

          return {
            isValid,
            msg: isValid
              ? ''
              : '외화보험 적합성 진단을 수행하지 않아 결과 확인이 불가합니다.',
            isMoveBack,
          };
        }
        return {
          isValid: true,
          msg: '',
          isMoveBack,
        };
      };
      const validationResult = validateStep(
        customerInfo,
        completedStepList,
        targetStep,
      );

      if (validationResult.isValid) {
        if (
          3 === currentStep.main &&
          3 !== targetStep.main &&
          validationResult.isMoveBack
        ) {
          showModal({
            visible: true,
            type: 'confirm',
            msg: '다른 단계로 이동시 작성내용이 수정됩니다.\n작성된 내용을 수정하시곘어요?',
            onOk: {
              label: '이동 후 수정',
              handle: () => {
                setCompletedStepList(prev => {
                  if ([1, 2].includes(targetStep.main)) {
                    return prev.filter(l => l.main === 1);
                  }
                  return prev;
                });
                setCurrentStep(targetStep);
              },
            },
          });
        } else if (isEmptyString(validationResult.msg)) {
          setCurrentStep(targetStep);
        }
      } else {
        showModal({
          visible: true,
          type: 'info',
          msg: validationResult.msg,
        });
      }
    },
    [],
  );

  return (
    <nav
      className={`nav app ${
        isPortrait ? (isShowLnb ? 'nav-vertical on' : 'nav-vertical') : ''
      }`}
    >
      <div className="tit-box yellow">
        <h3 className="insu-tit">외화보험 적합성 진단</h3>
        <ul className="customer-info">
          <li>{`고객명 : ${customerInfo?.name ?? ''} (${
            customerInfo?.insuranceAge ?? ''
          }/${customerInfo?.genderLabel ?? ''})`}</li>
        </ul>
      </div>
      <ul className="app-step">
        {stepList.map((step, idx) => (
          <li
            key={`appStep-${idx}`}
            className={
              isMainCompleted(completedStepList, stepList, idx)
                ? 'completed'
                : null
            }
          >
            <span
              className={`${currentStep.main === idx + 1 ? 'active' : ''} ${
                currentStep.main === idx + 1 && !isEmptyArray(step.subList)
                  ? 'open'
                  : ''
              }`}
              onClick={() =>
                handleMainClick(customerInfo, completedStepList, currentStep, {
                  main: idx + 1,
                  sub: 1,
                })
              }
            >
              {step.main}
            </span>
            {currentStep.main === idx + 1 && !isEmptyArray(step.subList) && (
              <ul className="sub-step">
                {step.subList?.map((s, subIdx) => (
                  <li key={`appStepSub-${idx}-${subIdx}`}>
                    <span
                      className={`
                        ${currentStep.sub === subIdx + 1 ? 'active' : null} ${
                        completedStepList?.some(
                          l => l.main === idx + 1 && l.sub === subIdx + 1,
                        )
                          ? 'complete'
                          : null
                      }`}
                      onClick={() =>
                        setCurrentStep({ main: idx + 1, sub: subIdx + 1 })
                      }
                    >
                      {s}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      <div className="nav-bottom">
        <button
          type="button"
          className="btn-s yellow btn-wide"
          onClick={() => handleCloseModalButtonClick()}
        >
          <span className="ico ico-exit2"></span>적합성진단 닫기
        </button>
      </div>
    </nav>
  );
};
ForeignCurrencySuitabilityTestLnb.propTypes = {
  customerInfo: PropTypes.object.isRequired,
  handleCloseModalButtonClick: PropTypes.func.isRequired,
  currentStep: PropTypes.shape({
    main: PropTypes.number.isRequired,
    sub: PropTypes.number,
  }).isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  completedStepList: PropTypes.arrayOf(
    PropTypes.shape({
      main: PropTypes.number, // 완료된 메인
      sub: PropTypes.number, // 완료된 서브
    }),
  ),
  setCompletedStepList: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  isPortrait: PropTypes.bool,
  isShowLnb: PropTypes.bool,
};

ForeignCurrencySuitabilityTestLnb.defaultProps = {
  completedStepList: [],
  isPortrait: false,
  isShowLnb: false,
};

export default memo(ForeignCurrencySuitabilityTestLnb);
